/**
 * Analytics tracking utility — fans out user identify + custom events to
 * Google Tag Manager (dataLayer) and Rudderstack (which routes to PostHog
 * + GA4). Implements B2B SaaS best practices for event tracking.
 */
import { rsAnalytics } from './rudderstack';

// Extend Window interface for GTM dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

// User properties for identification
interface UserProperties {
  userId?: string;
  email?: string;
  organizationId?: string;
  organizationName?: string;
  plan?: string;
  planTier?: 'trial' | 'starter' | 'professional' | 'enterprise' | 'free';
  subscriptionStatus?: 'trialing' | 'active' | 'past_due' | 'canceled' | 'trial_expired';
  trialDaysRemaining?: number;
  teamSize?: number;
  role?: string;
}

// Event categories following B2B SaaS best practices
type EventCategory = 
  | 'user_lifecycle'        // signup, login, trial_start, trial_end, etc.
  | 'subscription'          // created, upgraded, downgraded, cancelled
  | 'revenue'               // payment_succeeded, payment_failed, refund
  | 'product_usage'         // call_made, message_sent, contact_imported
  | 'engagement'            // feature_used, settings_updated
  | 'conversion'            // trial_to_paid, lead_to_customer
  | 'retention';            // churned, reactivated

interface AnalyticsEvent {
  event: string;
  category: EventCategory;
  action: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

class Analytics {
  private initialized = false;

  constructor() {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      this.initialized = true;
    }
  }

  /**
   * Push raw event to dataLayer
   */
  private push(data: any) {
    if (!this.initialized || typeof window === 'undefined') {
      console.warn('[Analytics] GTM not initialized');
      return;
    }
    
    try {
      window.dataLayer.push(data);
      console.log('[Analytics] Event tracked:', data.event || data);
    } catch (error) {
      console.error('[Analytics] Error pushing event:', error);
    }
  }

  /**
   * Identify user with properties.
   *
   * Fans out to:
   *   1. GTM dataLayer (`event: 'user_identify'`) — for GA4 user-id binding.
   *   2. Rudderstack (`rsAnalytics.identify`) — which also mirrors into
   *      `posthog.identify()` (see client/src/lib/rudderstack.ts), so PostHog
   *      session replays + exception events get a real distinct_id and the
   *      email/org/plan traits land on the person record.
   *
   * Without (2), every PostHog event for an authenticated user stays
   * anonymous (auto-UUID distinct_id) — which is what we observed before
   * this fix: of all persons with $exception events, none had email/org_id
   * properties so we couldn't tie an error back to an org.
   */
  identify(properties: UserProperties) {
    this.push({
      event: 'user_identify',
      ...properties,
    });

    if (properties.userId) {
      try {
        const { userId, ...traits } = properties;
        // Drop undefined values so PostHog person properties don't gain
        // null-valued keys.
        const cleanedTraits = Object.fromEntries(
          Object.entries(traits).filter(([, v]) => v !== undefined && v !== null),
        );
        rsAnalytics.identify(userId, cleanedTraits);
      } catch (err) {
        if (typeof console !== 'undefined') {
          console.warn('[Analytics] rsAnalytics.identify failed', err);
        }
      }
    }
  }

  /**
   * Track page view
   */
  trackPageView(pageName: string, pageCategory: string, userId?: string, organizationId?: string, additionalProps?: Record<string, any>) {
    this.push({
      event: 'page_view',
      pageName,
      pageCategory,
      userId,
      organizationId,
      timestamp: new Date().toISOString(),
      ...additionalProps,
    });
  }

  /**
   * Track custom event
   */
  track(eventData: AnalyticsEvent) {
    this.push({
      ...eventData,
      timestamp: new Date().toISOString(),
    });
  }

  // ===== USER LIFECYCLE EVENTS =====

  /**
   * User signed up
   */
  trackSignup(userId: string, organizationId: string, signupMethod?: string) {
    this.track({
      event: 'user_signup',
      category: 'user_lifecycle',
      action: 'signup',
      userId,
      organizationId,
      signupMethod: signupMethod || 'email',
    });
  }

  /**
   * User logged in
   */
  trackLogin(userId: string, organizationId: string, loginMethod?: string) {
    this.track({
      event: 'user_login',
      category: 'user_lifecycle',
      action: 'login',
      userId,
      organizationId,
      loginMethod: loginMethod || 'email',
    });
  }

  /**
   * Trial started
   */
  trackTrialStarted(userId: string, organizationId: string, planType: string, trialDays: number = 14) {
    this.track({
      event: 'trial_started',
      category: 'user_lifecycle',
      action: 'trial_start',
      userId,
      organizationId,
      planType,
      trialDays,
      value: trialDays,
    });
  }

  /**
   * Trial activated (first real usage)
   */
  trackTrialActivated(userId: string, organizationId: string, phoneNumber?: string) {
    this.track({
      event: 'trial_activated',
      category: 'user_lifecycle',
      action: 'trial_activated',
      userId,
      organizationId,
      phoneNumber,
    });
  }

  /**
   * Trial expired
   */
  trackTrialExpired(userId: string, organizationId: string, daysActive: number) {
    this.track({
      event: 'trial_expired',
      category: 'user_lifecycle',
      action: 'trial_expired',
      userId,
      organizationId,
      daysActive,
    });
  }

  // ===== SUBSCRIPTION EVENTS =====

  /**
   * Subscription created
   */
  trackSubscriptionCreated(userId: string, organizationId: string, planType: string, billingInterval: string, amount: number) {
    this.track({
      event: 'subscription_created',
      category: 'subscription',
      action: 'created',
      userId,
      organizationId,
      planType,
      billingInterval,
      amount,
      value: amount,
    });
  }

  /**
   * Subscription upgraded
   */
  trackSubscriptionUpgraded(userId: string, organizationId: string, fromPlan: string, toPlan: string, newAmount: number) {
    this.track({
      event: 'subscription_upgraded',
      category: 'subscription',
      action: 'upgraded',
      userId,
      organizationId,
      fromPlan,
      toPlan,
      newAmount,
      value: newAmount,
    });
  }

  /**
   * Subscription downgraded
   */
  trackSubscriptionDowngraded(userId: string, organizationId: string, fromPlan: string, toPlan: string, newAmount: number) {
    this.track({
      event: 'subscription_downgraded',
      category: 'subscription',
      action: 'downgraded',
      userId,
      organizationId,
      fromPlan,
      toPlan,
      newAmount,
      value: newAmount,
    });
  }

  /**
   * Subscription cancelled
   */
  trackSubscriptionCancelled(userId: string, organizationId: string, planType: string, cancelReason?: string) {
    this.track({
      event: 'subscription_cancelled',
      category: 'subscription',
      action: 'cancelled',
      userId,
      organizationId,
      planType,
      cancelReason,
    });
  }

  // ===== REVENUE EVENTS =====

  /**
   * Payment succeeded
   */
  trackPaymentSucceeded(userId: string, organizationId: string, amount: number, planType: string, invoiceId?: string) {
    this.track({
      event: 'payment_succeeded',
      category: 'revenue',
      action: 'payment_success',
      userId,
      organizationId,
      amount,
      planType,
      invoiceId,
      value: amount,
    });
  }

  /**
   * Payment failed
   */
  trackPaymentFailed(userId: string, organizationId: string, amount: number, planType: string, failureReason?: string) {
    this.track({
      event: 'payment_failed',
      category: 'revenue',
      action: 'payment_failure',
      userId,
      organizationId,
      amount,
      planType,
      failureReason,
      value: amount,
    });
  }

  /**
   * Refund issued
   */
  trackRefund(userId: string, organizationId: string, amount: number, reason?: string) {
    this.track({
      event: 'refund_issued',
      category: 'revenue',
      action: 'refund',
      userId,
      organizationId,
      amount,
      reason,
      value: amount,
    });
  }

  // ===== PRODUCT USAGE EVENTS =====

  /**
   * Call made
   */
  trackCallMade(userId: string, organizationId: string, callType: 'inbound' | 'outbound', duration?: number) {
    this.track({
      event: 'call_made',
      category: 'product_usage',
      action: 'call',
      userId,
      organizationId,
      callType,
      duration,
      value: duration,
    });
  }

  /**
   * Message sent
   */
  trackMessageSent(userId: string, organizationId: string, messageType: 'sms' | 'mms', isBulk: boolean = false) {
    this.track({
      event: 'message_sent',
      category: 'product_usage',
      action: 'message',
      userId,
      organizationId,
      messageType,
      isBulk,
    });
  }

  /**
   * Contact imported
   */
  trackContactImported(userId: string, organizationId: string, count: number, source?: string) {
    this.track({
      event: 'contacts_imported',
      category: 'product_usage',
      action: 'import_contacts',
      userId,
      organizationId,
      count,
      source,
      value: count,
    });
  }

  /**
   * Team member invited
   */
  trackTeamMemberInvited(userId: string, organizationId: string, invitedEmail: string, role: string) {
    this.track({
      event: 'team_member_invited',
      category: 'product_usage',
      action: 'invite_member',
      userId,
      organizationId,
      invitedEmail,
      role,
    });
  }

  /**
   * Phone number purchased
   */
  trackPhoneNumberPurchased(userId: string, organizationId: string, phoneNumber: string, numberType: string, cost: number) {
    this.track({
      event: 'phone_number_purchased',
      category: 'product_usage',
      action: 'purchase_number',
      userId,
      organizationId,
      phoneNumber,
      numberType,
      cost,
      value: cost,
    });
  }

  /**
   * Campaign created
   */
  trackCampaignCreated(userId: string, organizationId: string, campaignType: string, recipientCount: number) {
    this.track({
      event: 'campaign_created',
      category: 'product_usage',
      action: 'create_campaign',
      userId,
      organizationId,
      campaignType,
      recipientCount,
      value: recipientCount,
    });
  }

  // ===== ENGAGEMENT EVENTS =====

  /**
   * Feature used
   */
  trackFeatureUsed(userId: string, organizationId: string, featureName: string, context?: string) {
    this.track({
      event: 'feature_used',
      category: 'engagement',
      action: 'use_feature',
      userId,
      organizationId,
      featureName,
      context,
    });
  }

  /**
   * Settings updated
   */
  trackSettingsUpdated(userId: string, organizationId: string, settingType: string, settingName: string) {
    this.track({
      event: 'settings_updated',
      category: 'engagement',
      action: 'update_settings',
      userId,
      organizationId,
      settingType,
      settingName,
    });
  }

  // ===== CONVERSION EVENTS =====

  /**
   * Trial to paid conversion
   */
  trackTrialToPaidConversion(userId: string, organizationId: string, planType: string, amount: number, daysInTrial: number) {
    this.track({
      event: 'trial_to_paid_conversion',
      category: 'conversion',
      action: 'trial_convert',
      userId,
      organizationId,
      planType,
      amount,
      daysInTrial,
      value: amount,
    });
  }

  /**
   * Onboarding completed
   */
  trackOnboardingCompleted(userId: string, organizationId: string, timeToComplete: number) {
    this.track({
      event: 'onboarding_completed',
      category: 'engagement',
      action: 'complete_onboarding',
      userId,
      organizationId,
      timeToComplete,
      value: timeToComplete,
    });
  }

}

// Export singleton instance
export const analytics = new Analytics();
